import * as i0 from '@angular/core';
import { Pipe, Input, NgModule } from '@angular/core';
import * as i1 from '@fgb/core';
import { config, getCurrencyCode } from '@fgb/core';
import * as i1$1 from '@angular/platform-browser';
import * as markdown from 'marked';
import * as i1$2 from '@angular/common';
import { formatNumber } from '@angular/common';
import { isObservable, of } from 'rxjs';
import { map, startWith, catchError } from 'rxjs/operators';
import moment from 'moment/moment';
import * as entities from 'entities';

class ContentImagePipe {
    constructor(memberQuery) {
        this.memberQuery = memberQuery;
    }
    transform(value, variantId, loyaltyType) {
        if (!value) {
            if (!loyaltyType) {
                return `${config.contentUrl}/images/noimage.png`;
            }
            else {
                switch (loyaltyType) {
                    case 'marketplace': {
                        return 'assets/images/placeholders/marketplace-item.png';
                    }
                    case 'auction': {
                        return 'assets/images/placeholders/auction-item.png';
                    }
                    case 'lotto': {
                        return 'assets/images/placeholders/lotto-item.png';
                    }
                    case 'wallet': {
                        return 'assets/images/placeholders/marketplace-item.png';
                    }
                    case 'badge': {
                        return 'assets/images/placeholders/badge-item.png';
                    }
                    default: {
                        return 'assets/images/placeholders/marketplace-item.svg';
                    }
                }
            }
        }
        else {
            let currentUser = this.memberQuery.getValue().user || { clubId: config.defaultClubId, lcid: config.defaultLcid };
            const imageName = value.substring(0, value.indexOf('.'));
            const fileExtension = value.substring(value.indexOf('.'));
            return `${config.contentUrl}/image/${imageName}__${currentUser.clubId}_${variantId}_${currentUser.lcid}${fileExtension}`;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ContentImagePipe, deps: [{ token: i1.MemberQuery }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: ContentImagePipe, name: "contentImage" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ContentImagePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'contentImage' }]
        }], ctorParameters: () => [{ type: i1.MemberQuery }], propDecorators: { loyaltyType: [{
                type: Input
            }] } });

class SafeHtmlPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(htmlText) {
        return this.sanitizer.bypassSecurityTrustHtml(htmlText);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SafeHtmlPipe, deps: [{ token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: SafeHtmlPipe, name: "safeHtml" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SafeHtmlPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'safeHtml' }]
        }], ctorParameters: () => [{ type: i1$1.DomSanitizer }] });

class CreditCardNumberPipe {
    constructor() { }
    transform(value) {
        if (typeof value === 'string') {
            let cardNumber = value.replace(' ', '').replace('-', '');
            if (cardNumber.length === 16) {
                return '**** **** **** ' + value.substr(12);
            }
        }
        return value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CreditCardNumberPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: CreditCardNumberPipe, name: "creditCardNumber" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CreditCardNumberPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'creditCardNumber' }]
        }], ctorParameters: () => [] });

class CardTypeImageUrlPipe {
    constructor() { }
    transform(value) {
        return config.contentUrl + `/images/providers/cardType/${value}.svg`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CardTypeImageUrlPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: CardTypeImageUrlPipe, name: "cardTypeImageUrl" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CardTypeImageUrlPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'cardTypeImageUrl' }]
        }], ctorParameters: () => [] });

/**
 * This pipe is used to transform
 * markdown string to html.
 * To markdown translated text to html
 * you must have the translate and markdownTranslate
 * pipe in the correct order, as shown in
 * the example below. This will then apply the
 * markdown pipe to the translated text.
 *
 * Example of usage:
 * [innerHTML]="'registration.success.text' | translate | markdownTranslate"
 */
class MarkdownTranslatePipe {
    transform(value) {
        if (value && value.length > 0) {
            return markdown.marked(value);
        }
        return value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: MarkdownTranslatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: MarkdownTranslatePipe, name: "markdownTranslate" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: MarkdownTranslatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'markdownTranslate',
                }]
        }] });

class FortressCurrencyPipe {
    constructor(currencyPipe) {
        this.currencyPipe = currencyPipe;
        this.currenyCode = getCurrencyCode();
    }
    transform(value, currencyCode, display, digitsInfo, locale) {
        if (currencyCode && currencyCode === 'PTS') {
            value = formatNumber(value, 'en-GB', '1.0-0');
            return value + ' Pts';
        }
        else {
            const displayInCurrency = currencyCode ? display : 'symbol-narrow';
            const code = currencyCode ?? this.currenyCode;
            return this.currencyPipe.transform(value, code, displayInCurrency, digitsInfo, locale);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FortressCurrencyPipe, deps: [{ token: i1$2.CurrencyPipe }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: FortressCurrencyPipe, name: "FortressCurrencyPipe" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FortressCurrencyPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'FortressCurrencyPipe' }]
        }], ctorParameters: () => [{ type: i1$2.CurrencyPipe }] });

const defaultError = 'Something went wrong';
class WithLoadingPipe {
    transform(val) {
        return isObservable(val)
            ? val.pipe(map((value) => {
                return {
                    isLoading: value?.type === 'start',
                    error: value?.type === 'error' ? defaultError : '',
                    data: value?.type ? value.data : value,
                    isEmpty: !value,
                };
            }), startWith({ isLoading: true }), catchError((error) => of({ isLoading: false, error: typeof error === 'string' ? error : defaultError })))
            : val;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: WithLoadingPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: WithLoadingPipe, name: "withLoading" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: WithLoadingPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'withLoading' }]
        }] });

/**
 * This pipe is used to transform
 * the long text into short text.
 * it will take the numeric value as limit and then add (...) after the text
 *
 * Example of usage:
 * <p> {{ 'Hello world' | shortText: 5}} </p>"
 * Result Hello...
 */
class LongtoShortTextPipe {
    transform(value, limit, showDots = false) {
        return value.length > limit ? (showDots ? value.substring(0, limit) + '...' : value.substring(0, limit)) : value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: LongtoShortTextPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: LongtoShortTextPipe, name: "shortText" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: LongtoShortTextPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'shortText',
                }]
        }] });

/**
 * This pipe is used to get the content by lcid
 * it will take the list of content and lcid and return
 * the contact that match the lcid otherwise return the
 * firts one
 * Example of usage:
 * <p> {{  let content of offerContentList | currentLanguageContent: 2057}} </p>"
 */
class ContentLanguageFilterPipe {
    transform(contents, lcid, callFor) {
        callFor = callFor.toLowerCase();
        if (!contents)
            return 'n/a';
        if (!contents?.some((x) => x.LCID == +lcid)) {
            return callFor == 'name' ? contents[0].Name : callFor == 'description' ? contents[0].Description : 'n/a';
        }
        let langContent = contents?.filter((content) => {
            return content.LCID == +lcid;
        });
        if (langContent)
            return callFor == 'name' ? langContent[0].Name : callFor == 'description' ? langContent[0].Description : 'n/a';
        return 'n/a';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ContentLanguageFilterPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: ContentLanguageFilterPipe, name: "currentLanguageContent" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ContentLanguageFilterPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'currentLanguageContent',
                }]
        }] });

class UtcToLocaltimePipe {
    transform(value, format = 'MMMM D, YYYY hh:mm a') {
        if (value) {
            return moment.utc(value).local().format(format);
        }
        return value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UtcToLocaltimePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: UtcToLocaltimePipe, name: "UtcToLocaltimePipe" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UtcToLocaltimePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'UtcToLocaltimePipe' }]
        }] });

const DEFAULT_LIMIT = 20;
const DEFAULT_TRAIL = '...';
/**
 * Truncates a string to a specified length and appends a trail
 * by default it will truncate to 20 characters and append '...'
 *
 * @param value - string to truncate
 * @param limit - number of characters to truncate to
 * @param trail - string to append to the end of the truncated string
 * @param type - type of string to truncate 'text' | 'html' - default is 'text'
 *
 * using text type
 *    ex: {{ 'some string' | truncate: 10: '...' }}
 *
 * using html type
 *    ex: <div [innerHTML]="'some html string' | truncate: 10: '...' : 'html'"></div>
 */
class TruncatePipe {
    transform(value, limit, trail, type = 'text') {
        if (!value) {
            return '';
        }
        limit = limit || DEFAULT_LIMIT;
        trail = trail || DEFAULT_TRAIL;
        // If type is html convert html to plain text and truncate
        if (type === 'html') {
            return this.htmlToPlainText(value).length > limit ? this.truncateHtml(value, limit, trail) : value;
        }
        // If type is text truncate
        return value.length > limit ? value.substring(0, limit) + trail : value;
    }
    // Converts html to plain text
    htmlToPlainText(html) {
        const code = /<.*?>|<\/.*?>/g; // strips out code
        return entities.decodeHTML(html.replace(code, ''));
    }
    // Truncates html string while preserving styles and html tags
    truncateHtml(value, limit, trail) {
        let str = '';
        let count = 0;
        const openTag = '<';
        const closeTag = '>';
        const endTag = '/';
        let tags = [];
        // Loop through each character in the string
        for (let i = 0; i < value.length; i++) {
            // If character is an open tag
            if (value[i] === openTag) {
                // Get the tag
                const tag = value.substring(i, value.substring(i, value.length).indexOf(closeTag) + i + 1);
                // Get the tag name
                // ex: <div class="some-class" id="some-id"> => div
                const tagName = tag.split(' ')[0].replace(/<|>/g, '');
                // If tag is not a self closing tag and is not an end tag add it to the stack else remove it from the stack
                if (tag.indexOf(endTag) === -1) {
                    tags.push(tagName);
                }
                else {
                    tags.pop();
                }
                // Append tag to the string
                str += tag;
                // Increment i by the length of the tag
                i += tag.length - 1;
            }
            else {
                // Since & is already replaced with &amp; entity we can assume that anything that starts with & is an entity
                if (value[i] === '&') {
                    // Get the entity
                    const entity = value.substring(i, value.substring(i, value.length).indexOf(';') + i + 1);
                    // Append entity to the string
                    str += entity;
                    // Increment i by the length of the entity
                    i += entity.length - 1;
                }
                else {
                    str += value[i];
                }
                count++;
            }
            // If count is equal to the limit append the trail to the string and break out of the loop
            if (count === limit) {
                // Append trail to the string
                str += `<span class="truncate-dots" style="color: #000;">${trail}</span>`;
                // If tags are unclosed append tags to the end of the string
                if (tags.length > 0) {
                    for (let x = tags.length; x > 0; x--) {
                        str += `${openTag}${endTag}${tags.pop()}${closeTag}`;
                    }
                }
                break;
            }
        }
        return str;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: TruncatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: TruncatePipe, name: "truncate" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: TruncatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'truncate',
                }]
        }] });

class PipesModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: PipesModule, declarations: [ContentImagePipe,
            SafeHtmlPipe,
            CardTypeImageUrlPipe,
            CreditCardNumberPipe,
            MarkdownTranslatePipe,
            FortressCurrencyPipe,
            WithLoadingPipe,
            LongtoShortTextPipe,
            ContentLanguageFilterPipe,
            UtcToLocaltimePipe,
            TruncatePipe], exports: [ContentImagePipe,
            SafeHtmlPipe,
            CardTypeImageUrlPipe,
            CreditCardNumberPipe,
            MarkdownTranslatePipe,
            FortressCurrencyPipe,
            WithLoadingPipe,
            LongtoShortTextPipe,
            ContentLanguageFilterPipe,
            UtcToLocaltimePipe,
            TruncatePipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PipesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PipesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ContentImagePipe,
                        SafeHtmlPipe,
                        CardTypeImageUrlPipe,
                        CreditCardNumberPipe,
                        MarkdownTranslatePipe,
                        FortressCurrencyPipe,
                        WithLoadingPipe,
                        LongtoShortTextPipe,
                        ContentLanguageFilterPipe,
                        UtcToLocaltimePipe,
                        TruncatePipe
                    ],
                    exports: [
                        ContentImagePipe,
                        SafeHtmlPipe,
                        CardTypeImageUrlPipe,
                        CreditCardNumberPipe,
                        MarkdownTranslatePipe,
                        FortressCurrencyPipe,
                        WithLoadingPipe,
                        LongtoShortTextPipe,
                        ContentLanguageFilterPipe,
                        UtcToLocaltimePipe,
                        TruncatePipe
                    ],
                    imports: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CardTypeImageUrlPipe, ContentImagePipe, ContentLanguageFilterPipe, CreditCardNumberPipe, FortressCurrencyPipe, LongtoShortTextPipe, MarkdownTranslatePipe, PipesModule, SafeHtmlPipe, TruncatePipe, UtcToLocaltimePipe, WithLoadingPipe };
